import { FilterItem } from "@via/pages/data/premium-dataset/utils/helper";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import HowItWorksHelp from "@via/components/how-it-works/how-it-works-help";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export const SEARCH_DEBOUNCE_MS = 300;

type PremiumDatasetFileFilterProps = {
  columns: any[];
  columnFilters: { or: FilterItem[] };
  setColumnFilters: (
    filters: (prevFilters: { or: FilterItem[] }) => { or: FilterItem[] }
  ) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  rightActions?: ReactNode;
  howItWorks?: {
    title: ReactNode;
    content: ReactNode;
  };
  /** Turns the search off, for example while one record is pinned. */
  disabled?: boolean;
  /** Shown under the input while it is disabled. */
  disabledHint?: string;
};

const PremiumDatasetFileFilter = ({
  columns,
  columnFilters,
  setColumnFilters,
  setPage,
  setPageSize,
  rightActions,
  howItWorks,
  disabled = false,
  disabledHint,
}: PremiumDatasetFileFilterProps) => {
  const appliedKeyword =
    columnFilters?.or?.length > 0
      ? String(columnFilters.or[0].value ?? "")
      : "";
  const [text, setText] = useState(appliedKeyword);
  // One timer for the whole component life, so a search typed in an earlier render can still be cancelled.
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPending = useCallback(() => {
    if (pending.current !== null) {
      clearTimeout(pending.current);
      pending.current = null;
    }
  }, []);

  const applySearch = (value: string): void => {
    setColumnFilters(() => {
      if (!value.trim()) {
        return { or: [] };
      }
      const orFilters: FilterItem[] = columns.map(column => ({
        id: column.accessorKey,
        value,
      }));
      return { or: orFilters };
    });
    setPage(0);
    setPageSize(10);
  };

  const onType = (value: string): void => {
    setText(value);
    cancelPending();
    pending.current = setTimeout(() => {
      pending.current = null;
      applySearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  // The box shows what the filters hold: when a parent clears them, the text goes too. A search
  // still waiting to apply keeps what the person typed.
  useEffect(() => {
    if (pending.current === null) setText(appliedKeyword);
  }, [appliedKeyword]);

  // Turning the search off drops anything typed but not applied, so no late callback can bring
  // filters back or reset the page size.
  useEffect(() => {
    if (disabled) {
      cancelPending();
      setText("");
    }
  }, [disabled, cancelPending]);

  useEffect(() => cancelPending, [cancelPending]);

  return (
    <div className="w-full">
      <div className="flex w-full items-start gap-3">
        <div className="mt-2 flex-grow relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <input
            type="text"
            placeholder="Type to search"
            className="w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-50"
            value={text}
            disabled={disabled}
            aria-disabled={disabled}
            onChange={e => onType(e.target.value)}
          />
        </div>

        {howItWorks && (
          <div className="mt-2 shrink-0">
            <HowItWorksHelp title={howItWorks.title}>
              {howItWorks.content}
            </HowItWorksHelp>
          </div>
        )}

        {rightActions && <div className="mt-2 shrink-0">{rightActions}</div>}
      </div>
      {disabled && disabledHint ? (
        <p className="mt-1 text-xs text-gray-500">{disabledHint}</p>
      ) : null}
    </div>
  );
};

export default PremiumDatasetFileFilter;
